import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2, Database } from "lucide-react";

const STATUS_CFG = {
  ok: { label: "Actueel", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  achterstand: { label: "Achterstand", cls: "bg-amber-100 text-amber-800", Icon: AlertTriangle },
  fout: { label: "Fout", cls: "bg-rose-100 text-rose-800", Icon: XCircle },
  bezig: { label: "Bezig...", cls: "bg-blue-100 text-blue-800", Icon: Loader2 },
  onbekend: { label: "Onbekend", cls: "bg-slate-100 text-slate-700", Icon: AlertTriangle },
};

const relTijd = (iso) => {
  if (!iso) return "nooit";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "zojuist";
  if (min < 60) return `${min} min geleden`;
  const uur = Math.floor(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  return `${Math.floor(uur / 24)} dag(en) geleden`;
};

const SYNC_CALLS = {
  werkbonnen: () => api.post("/outsmart/sync-werkbonnen-alle-entiteiten"),
  offertes: () => api.post("/outsmart/sync-offertes-alle-entiteiten"),
  facturen: () => api.post("/sync/facturen-nu"),
};

export const SyncStatusPaneel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [debStatus, setDebStatus] = useState(null);
  const [debBusy, setDebBusy] = useState(false);

  const loadDebiteur = async () => {
    try {
      const { data } = await api.get("/vaste-klanten/debiteur-backfill/status");
      setDebStatus(data);
    } catch { /* geen manager */ }
  };

  const startDebiteurBackfill = async () => {
    setDebBusy(true);
    try {
      const { data } = await api.post("/vaste-klanten/debiteur-backfill/run");
      toast[data.ok ? "success" : "info"](data.melding || data.reden || "Gestart");
      setTimeout(loadDebiteur, 8000);
      setTimeout(loadDebiteur, 25000);
    } catch { toast.error("Backfill starten mislukt"); }
    setDebBusy(false);
  };

  const load = async (spin = false) => {
    if (spin) setBusy(true);
    try {
      const { data } = await api.get("/sync/status-overzicht");
      setData(data);
    } catch { /* geen manager of endpoint niet beschikbaar */ }
    if (spin) setBusy(false);
  };

  useEffect(() => {
    load();
    loadDebiteur();
    const iv = setInterval(() => load(false), 60000);
    return () => clearInterval(iv);
  }, []);

  const syncNu = async (bron) => {
    setSyncing((s) => ({ ...s, [bron]: true }));
    try {
      await SYNC_CALLS[bron]();
      toast.success(`Sync ${bron} gestart — dit kan enkele minuten duren`);
      setTimeout(() => load(false), 5000);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setSyncing((s) => ({ ...s, [bron]: false }));
  };

  if (!data) return null;
  const heeftWaarschuwing = data.bronnen.some((b) => b.status === "achterstand" || b.status === "fout");

  return (
    <Card className={`p-4 ${heeftWaarschuwing ? "border-amber-300 bg-amber-50/40" : ""}`} data-testid="sync-status-paneel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-slate-600" />
          <h3 className="font-semibold text-sm text-slate-900">Sync-status databronnen</h3>
          {heeftWaarschuwing && (
            <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid="sync-waarschuwing-badge">
              <AlertTriangle className="h-3 w-3 mr-0.5" /> Aandacht nodig
            </Badge>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => load(true)} disabled={busy} data-testid="sync-status-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="space-y-2">
        {data.bronnen.map((b) => {
          const cfg = STATUS_CFG[b.status] || STATUS_CFG.onbekend;
          const { Icon } = cfg;
          return (
            <div key={b.bron}
                 className="flex items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2"
                 data-testid={`sync-bron-${b.bron}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">{b.label}</div>
                <div className="text-[11px] text-slate-500">
                  Laatste sync: <b>{relTijd(b.laatste_sync)}</b>
                  {b.status === "achterstand" && (
                    <span className="text-amber-700 ml-1">
                      (ouder dan {b.drempel_uren} uur — mogelijk niet actueel)
                    </span>
                  )}
                  {b.fout && <span className="text-rose-700 ml-1">— {b.fout}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge className={`${cfg.cls} text-[10px]`} data-testid={`sync-status-${b.bron}`}>
                  <Icon className={`h-3 w-3 mr-0.5 ${b.status === "bezig" ? "animate-spin" : ""}`} /> {cfg.label}
                </Badge>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => syncNu(b.bron)}
                        disabled={syncing[b.bron] || b.status === "bezig"}
                        data-testid={`sync-nu-${b.bron}`}>
                  {syncing[b.bron] ? "..." : "Sync nu"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {debStatus && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2"
             data-testid="debiteur-backfill-blok">
          <div className="text-xs text-slate-600">
            <span className="font-semibold">Debiteurnummers vaste klanten:</span>{" "}
            <span className="text-emerald-700 font-semibold">{debStatus.klanten_met_debiteurnummer} gekoppeld</span>
            {" · "}
            <span className={debStatus.klanten_zonder > 0 ? "text-amber-700" : "text-slate-400"}>
              {debStatus.klanten_zonder} zonder
            </span>
            {debStatus.laatste_run && (
              <span className="text-slate-400 ml-1">
                (laatste run: {(debStatus.laatste_run.gestart_op || "").split("T")[0]}, +{debStatus.laatste_run.gevuld} gevuld)
              </span>
            )}
            <span className="text-slate-400 ml-1">· nachtelijk 03:30</span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={startDebiteurBackfill}
                  disabled={debBusy || debStatus.bezig}
                  data-testid="debiteur-backfill-run">
            {debStatus.bezig ? "Bezig…" : debBusy ? "..." : "Nu aanvullen"}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default SyncStatusPaneel;
